/**
 * Text encodings, decoded the same on every runtime.
 *
 * Windows-1252 is decoded and encoded by the WHATWG Encoding Standard's
 * index (index-windows-1252) with a table of its own, not by the runtime's
 * `TextDecoder`: some Node.js versions decode it as Latin-1
 * (nodejs/node#60888; 22.13.0 to 22.22.0, 23.x, 24.0.0 to 24.13.0 and 25.0.0
 * to 25.3.x), so every character of 0x80–0x9F (€, the dashes, the
 * typographic quotes) comes out as a control character. Every label the
 * standard maps to windows-1252 (latin1, iso-8859-1, us-ascii, cp1252, …) is
 * decoded by the table. Every other encoding is the runtime's.
 */

/** The characters of 0x80–0x9F by index-windows-1252 (pointers 0–31), which lists 0x81, 0x8D, 0x8F, 0x90 and 0x9D as the C1 control of the same number. */
const HIGH: readonly number[] = [
  0x20ac, 0x0081, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, 0x008d, 0x017d, 0x008f,
  0x0090, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x009d, 0x017e, 0x0178,
];

/** The byte of each code point the index lists for 0x80–0x9F (pointer + 0x80), the five C1 controls included. */
const HIGH_BYTE_OF: ReadonlyMap<number, number> = new Map(HIGH.map((codePointArg, indexArg): [number, number] => [codePointArg, 0x80 + indexArg]));

const CHUNK = 4096;

/** Decodes bytes as Windows-1252 by the Encoding Standard's index. Every byte decodes; nothing is refused. */
export const decodeWindows1252 = (bytesArg: Uint8Array): string => {
  let text = '';
  for (let offset = 0; offset < bytesArg.length; offset += CHUNK) {
    const chunk = bytesArg.subarray(offset, offset + CHUNK);
    text += String.fromCharCode(...Array.from(chunk, (byteArg) => (byteArg >= 0x80 && byteArg <= 0x9f ? HIGH[byteArg - 0x80] : byteArg)));
  }
  return text;
};

/** The byte of a code point in Windows-1252, or undefined where the encoding cannot write it. */
export const windows1252ByteOf = (codePointArg: number): number | undefined => {
  if (codePointArg <= 0x7f || (codePointArg >= 0xa0 && codePointArg <= 0xff)) {
    return codePointArg;
  }
  return HIGH_BYTE_OF.get(codePointArg);
};

/** A character that was written as `?`, with its position in the text (UTF-16 offset). */
export interface IReplacedCharacter {
  offset: number;
  character: string;
}

/**
 * Encodes text in Windows-1252 by the Encoding Standard's single-byte
 * encoder: an ASCII code point is its byte, any other the index's pointer
 * plus 0x80 (the five C1 controls the index maps, U+0081, U+008D, U+008F,
 * U+0090 and U+009D, included). A character the index does not list becomes
 * one `?` (one per character, a surrogate pair included) and is listed in
 * `replaced`, so the caller can say which text lost which character.
 */
export const encodeWindows1252 = (textArg: string): { bytes: Uint8Array; replaced: IReplacedCharacter[] } => {
  const bytes: number[] = [];
  const replaced: IReplacedCharacter[] = [];
  let offset = 0;
  for (const character of textArg) {
    const byte = windows1252ByteOf(character.codePointAt(0) ?? 0);
    if (byte === undefined) {
      bytes.push(0x3f);
      replaced.push({ offset, character });
    } else {
      bytes.push(byte);
    }
    offset += character.length;
  }
  return { bytes: Uint8Array.from(bytes), replaced };
};

/** Whether bytes are valid UTF-8 (a byte order mark included). */
export const isValidUtf8 = (bytesArg: Uint8Array): boolean => {
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(bytesArg);
    return true;
  } catch {
    return false;
  }
};

/**
 * The encoding a label names, by the Encoding Standard's registry (as the
 * runtime reads labels, `TextDecoder.encoding`), or undefined for a label it
 * does not know. The registry strips ASCII whitespace (TAB, LF, FF, CR,
 * SPACE) around a label and nothing else, so a label with a no-break space
 * or a byte order mark before it is none. A label of the "replacement"
 * encoding (`iso-2022-kr`, `hz-gb-2312`, `iso-2022-cn`, `csiso2022kr`, …) is
 * none either: `TextDecoder` refuses it. `latin1`, `iso-8859-1` and
 * `us-ascii` name windows-1252.
 */
export const encodingOfLabel = (labelArg: string): string | undefined => {
  try {
    return new TextDecoder(labelArg).encoding;
  } catch {
    return undefined;
  }
};

/**
 * Decodes bytes in the encoding a label names: windows-1252 by the table,
 * any other by the runtime, which removes a byte order mark of its own
 * encoding. `fatal` refuses bytes that are not valid in the encoding (it
 * throws, as `TextDecoder` does); windows-1252 decodes every byte. Undefined
 * for a label the registry does not know.
 */
export const decodeText = (bytesArg: Uint8Array, labelArg: string, optionsArg: { fatal?: boolean } = {}): string | undefined => {
  const encoding = encodingOfLabel(labelArg);
  if (encoding === undefined) {
    return undefined;
  }
  if (encoding === 'windows-1252') {
    return decodeWindows1252(bytesArg);
  }
  return new TextDecoder(encoding, { fatal: optionsArg.fatal ?? false }).decode(bytesArg);
};

/**
 * Decodes a file whose encoding is not declared the way German bank exports
 * and legacy files are written: UTF-8 when it is valid UTF-8 (a byte order
 * mark removed), else Windows-1252.
 */
export const decodeUtf8OrWindows1252 = (bytesArg: Uint8Array): { text: string; encoding: 'utf-8' | 'windows-1252' } => {
  if (isValidUtf8(bytesArg)) {
    return { text: new TextDecoder('utf-8').decode(bytesArg), encoding: 'utf-8' };
  }
  return { text: decodeWindows1252(bytesArg), encoding: 'windows-1252' };
};
